import { createBrowserClient, type SupabaseClient } from '@supabase/ssr';

// Singleton instance
let supabaseClient: SupabaseClient | null = null;

/**
 * Creates a singleton Supabase client for browser use.
 * This prevents multiple client instances which can cause:
 * - Memory leaks
 * - Multiple realtime connections
 * - Auth state inconsistencies
 */
export function createClient(): SupabaseClient {
  // Return existing instance if available
  if (supabaseClient) {
    return supabaseClient;
  }

  // Validate environment variables
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL environment variable');
  }

  if (!supabaseAnonKey) {
    throw new Error('Missing NEXT_PUBLIC_SUPABASE_ANON_KEY environment variable');
  }

  // Create new client
  supabaseClient = createBrowserClient(supabaseUrl, supabaseAnonKey, {
    cookies: {
      getAll() {
        // Guard against SSR - document is not available on the server
        if (typeof document === 'undefined') {
          return [];
        }

        return document.cookie
          .split(';')
          .filter(Boolean)
          .map((cookie) => {
            const [name, ...rest] = cookie.trim().split('=');
            return {
              name,
              value: decodeURIComponent(rest.join('=')),
            };
          });
      },

      setAll(cookiesToSet: Array<{ name: string; value: string; options?: Record<string, unknown> }>) {
        // Guard against SSR - document is not available on the server
        if (typeof document === 'undefined') {
          return;
        }

        cookiesToSet.forEach(({ name, value, options = {} }) => {
          const cookieParts = [
            `${name}=${encodeURIComponent(value)}`,
            `Path=${(options.path as string) || '/'}`,
            'SameSite=Lax', // Use Lax for better compatibility
          ];

          // Only set Secure in production or HTTPS
          if (
            typeof window !== 'undefined' &&
            window.location.protocol === 'https:'
          ) {
            cookieParts.push('Secure');
          }

          if (options.maxAge !== undefined) {
            cookieParts.push(`Max-Age=${options.maxAge}`);
          }

          if (options.domain) {
            cookieParts.push(`Domain=${options.domain}`);
          }

          if (options.expires) {
            const expiresDate =
              options.expires instanceof Date
                ? options.expires.toUTCString()
                : options.expires;
            cookieParts.push(`Expires=${expiresDate}`);
          }

          document.cookie = cookieParts.join('; ');
        });
      },
    },
    auth: {
      // Persist session in localStorage for better reliability
      persistSession: true,
      // Auto refresh token before expiry
      autoRefreshToken: true,
      // Detect session from URL (for OAuth callbacks)
      detectSessionInUrl: true,
    },
  });

  return supabaseClient;
}

/**
 * Clears the singleton instance.
 * Useful for testing or when you need to reset the client.
 */
export function clearClient(): void {
  supabaseClient = null;
}

/**
 * Type-safe helper to get the current user
 * Returns null if not authenticated
 */
export async function getCurrentUser() {
  const client = createClient();
  const { data: { user }, error } = await client.auth.getUser();
  
  if (error) {
    console.error('Error getting current user:', error);
    return null;
  }
  
  return user;
}

/**
 * Type-safe helper to check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser();
  return user !== null;
}
